// /features — full feature catalog with JTBD-framed descriptions and screenshot slots.
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import {
  BookOpen, Presentation, ScrollText, HelpCircle, Volume2, Sparkles, Zap,
  Gamepad2, Users, MessageCircle, Timer, Clock, Trophy, Languages, Award,
} from "lucide-react";

const FEATURE_ROWS = [
  {
    icon: BookOpen,
    title: "AI-narrated lectures",
    jtbd: "When I have a chapter to learn but no time to read it, I want a real lecture I can listen to.",
    body: "10-15 minute lectures generated by Gemini 3.1 Pro, narrated in a natural voice. Adjustable pace, English + Spanish, with citations back to the source.",
  },
  {
    icon: Presentation,
    title: "Slide decks with visuals",
    jtbd: "When I'm presenting or reviewing, I want visual slides — not just text.",
    body: "Every lecture ships with an AI-generated slide deck. Present in class, review as a slideshow, or export to PDF.",
  },
  {
    icon: ScrollText,
    title: "Printable study guides",
    jtbd: "When I want to study offline or on paper, I need a real study guide.",
    body: "Full page layouts with key terms, key concepts, end-of-chapter Q&A, and citations. Print, download as PDF, or read on any device.",
  },
  {
    icon: HelpCircle,
    title: "Graded interactive quizzes",
    jtbd: "When I've studied, I want to know if I actually understood it.",
    body: "Multiple choice + short answer quizzes with real letter grades, explanations on every question, and retry flow to master the tough ones.",
  },
  {
    icon: MessageCircle,
    title: "Ask the Professor",
    jtbd: "When something in the lecture confuses me, I want to ask a follow-up.",
    body: "Chat with the same AI that wrote your lecture. It knows the exact chapter context — no explaining what you're studying every time.",
  },
  {
    icon: Timer,
    title: "Focus Mode",
    jtbd: "When I have a big exam coming up, I want to study without distractions.",
    body: "Distraction-free study session with a built-in pomodoro timer, exam countdown, and progress tracking.",
  },
  {
    icon: Zap,
    title: "Quick Study",
    jtbd: "When I only have 5 minutes before a class or a quiz, I want a fast refresher.",
    body: "One-tap generation of a 3-minute audio recap + 5-question flash quiz from any lecture in your library.",
  },
  {
    icon: Gamepad2,
    title: "Live Class Game Mode",
    jtbd: "When I'm a teacher, I want to make review fun and see who's actually got it.",
    body: "Turn any quiz into a live multiplayer game. Students join with a code, avatars battle it out, real-time leaderboards.",
  },
  {
    icon: Users,
    title: "Classrooms &amp; rostering",
    jtbd: "When I'm running a class, I want to assign lessons and track who's completed them.",
    body: "Free classroom platform for verified educators. Roster students, push assignments, see completion + score data.",
  },
  {
    icon: Trophy,
    title: "Library &amp; sharing",
    jtbd: "When I've built a great study guide, I want to save it and share it.",
    body: "Every lecture saved to your library forever. Share any guide with a one-click public link — no signup needed to view.",
  },
  {
    icon: Languages,
    title: "Bilingual output",
    jtbd: "When English isn't my first language, I want lessons in both.",
    body: "Toggle any lecture into a side-by-side English/Spanish view. More languages coming.",
  },
  {
    icon: Award,
    title: "Standards-aligned",
    jtbd: "When I'm a teacher, I need lessons that map to my state's standards.",
    body: "Every output aligns to Common Core (ELA + Math), NGSS (Science), and TEKS (Texas). More standards on request.",
  },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Lectura Features: AI Lectures, Slides, Study Guides & Quizzes"
        description="AI-narrated lectures, slide decks, printable study guides, and graded quizzes from any textbook chapter. Standards-aligned. Powered by Gemini 3.1 Pro."
        canonical="https://getlectura.app/features"
      />
      <MarketingNav />
      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-accent">Features</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">One workflow. Everything a student needs.</h1>
            <p className="mt-4 text-muted-foreground">
              Name a book and chapter. Lectura runs the whole pipeline — lecture, slides, guide, quiz — in under two minutes.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {FEATURE_ROWS.map((f, i) => (
              <article key={i} className="rounded-2xl border border-border/60 p-6 hover:border-accent/60 transition" data-testid={`feature-row-${i}`}>
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 font-bold">{f.title}</h3>
                <p className="mt-1 text-xs italic text-muted-foreground">"{f.jtbd}"</p>
                <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
              </article>
            ))}
          </div>
          {/* FLAG: swap the icon-only feature cards for cards that embed real product screenshots. */}
        </section>

        <section className="border-t border-border/60 bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14 text-center">
            <h2 className="font-serif text-3xl font-bold">Try it free — no credit card required.</h2>
            <p className="mt-3 text-primary-foreground/80">Three free lectures. Cancel Pro anytime with a 30-day money-back guarantee.</p>
            <Link to="/register" className="mt-6 inline-flex rounded-full bg-accent text-accent-foreground px-6 py-3 font-black">
              Start free
            </Link>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
